import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { getStoreScopedPath } from "@redotech/redo-merchant-app-common/routing/store-scope";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getConnectedStores } from "@redotech/redo-merchant-login-app/client/auth";
import { TeamSwitcherLoginModal } from "@redotech/redo-merchant-login-app/team-switcher-login-modal";
import {
  ConnectedTeamData,
  CurrentTeamData,
  TeamDataContext,
  TeamSwitcherData,
} from "@redotech/redo-web/navigation/common/team-switcher-data";
import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { memo, useMemo, useState } from "react";
import { resolveTeamDisplayName } from "./team-switcher-name";

export const TeamSwitcherService = memo(function TeamSwitcherService({
  children,
}: {
  children: React.ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const [addStoreModalOpen, setAddStoreModalOpen] = useState(false);

  const client = useRequiredContext(RedoMerchantClientContext);
  const effectApi = useRequiredContext(EffectApiClientContext);

  const connectedStores = useLoad(
    async (signal) => {
      if (team) {
        return await getConnectedStores(client, { signal });
      }
      return [];
    },
    [team],
  );

  const hasConnectedStores = (connectedStores.value?.length || 0) > 0;

  // Fetch connected-team ticket counts via the effect-api RPC, polling every 60s
  const { data: v2ConnectedTeamCounts } = useQuery({
    queryKey: ["connectedTeamsCounts"],
    queryFn: async () => {
      const rpcClient = effectApi.getClient();
      return await effectApi.runPromise(
        rpcClient.connectedTeamsCounts
          .getConnectedTeamsCounts({})
          .pipe(
            Effect.catchAll(() => Effect.succeed<Record<string, number>>({})),
          ),
      );
    },
    enabled: hasConnectedStores,
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const connectedTeamCounts = useMemo(
    () => v2ConnectedTeamCounts ?? {},
    [v2ConnectedTeamCounts],
  );

  const handleTeamSwitch = useHandler((teamId: string) => {
    if (teamId === team._id) {
      return;
    }
    location.href = getStoreScopedPath(teamId, "");
  });

  const countTotal = Object.values(connectedTeamCounts).reduce(
    (sum: number, count: number) => sum + count,
    0,
  );

  const data: TeamSwitcherData = useMemo(() => {
    const currentTeamData: CurrentTeamData = {
      teamId: team._id,
      name: resolveTeamDisplayName(team),
      logoUrl: team.settings.brandKit?.images.logoUrl,
    };

    const connectedTeamData: ConnectedTeamData[] = (
      connectedStores.value || []
    ).map((connectedStore) => {
      return {
        teamId: connectedStore.teamId,
        name: resolveTeamDisplayName(connectedStore),
        logoUrl: connectedStore.logoUrl,
        ticketCount: connectedTeamCounts[connectedStore.teamId] || 0,
      };
    });

    return {
      connectedStoresHaveTickets: countTotal > 0,
      connectedStores: connectedTeamData,
      setCurrentTeam: handleTeamSwitch,
      currentTeam: currentTeamData,
      addStore: () => setAddStoreModalOpen(true),
    };
  }, [
    countTotal,
    connectedStores.value,
    connectedTeamCounts,
    team,
    handleTeamSwitch,
  ]);

  return (
    <TeamDataContext.Provider value={data}>
      <>
        {children}
        <TeamSwitcherLoginModal
          open={addStoreModalOpen}
          setOpen={setAddStoreModalOpen}
        />
      </>
    </TeamDataContext.Provider>
  );
});
